import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";

interface RatingItem {
  star: number;
  count: number;
  percentage: number;
}

interface NormalizedRow {
  star: number;
  percentage: number;
}

@Component({
  selector: "app-satisfaction-ratings",
  templateUrl: "./satisfaction-ratings.component.html",
  styleUrls: ["./satisfaction-ratings.component.scss"],
})
export class SatisfactionRatingsComponent implements OnChanges {
  @Input() organizationRating: RatingItem[];
  @Input() activitiesRating: RatingItem[];
  @Input() userComments: Array<{ userId: string; name: string; comment: string }>;
  @Input() userRateNotesCount: number;
  @Input() overallRating: number;
  @Input() attendeeCount: number;
  @Input() rateOf: number = 5;
  @Input() translationModulesEnum: any;
  @Input() currentLanguage: string;
  @Input() showLargeStars: boolean = false;

  fullStars: number[] = [];
  emptyStars: number[] = [];
  hasHalfStar = false;

  combinedBreakdown: NormalizedRow[] = [];
  normalizedOrgRating: NormalizedRow[] = [];
  normalizedActivitiesRating: NormalizedRow[] = [];

  ngOnChanges(_changes: SimpleChanges): void {
    this.calculateStars();
    this.buildBreakdowns();
  }

  private calculateStars(): void {
    const total = this.rateOf || 5;
    if (!this.overallRating || this.overallRating <= 0) {
      this.fullStars = [];
      this.emptyStars = Array(total).fill(0);
      this.hasHalfStar = false;
      return;
    }
    const clamped = Math.min(this.overallRating, total);
    const full = Math.floor(clamped);
    this.hasHalfStar = clamped % 1 >= 0.5;
    this.fullStars = Array(full).fill(0);
    this.emptyStars = Array(Math.max(0, total - full - (this.hasHalfStar ? 1 : 0))).fill(0);
  }

  private normalizeRating(arr: RatingItem[]): NormalizedRow[] {
    const total = this.rateOf || 5;
    return Array.from({ length: total }, (_, i) => {
      const star = i + 1;
      const item = arr?.find(r => r.star === star);
      return { star, percentage: item?.percentage ?? 0 };
    });
  }

  private buildBreakdowns(): void {
    this.normalizedOrgRating = this.normalizeRating(this.organizationRating);
    this.normalizedActivitiesRating = this.normalizeRating(this.activitiesRating);

    const hasOrg = this.organizationRating?.length > 0;
    const hasAct = this.activitiesRating?.length > 0;
    const total = this.rateOf || 5;

    this.combinedBreakdown = Array.from({ length: total }, (_, i) => {
      const star = i + 1;
      const orgPct = this.normalizedOrgRating.find(r => r.star === star)?.percentage ?? 0;
      const actPct = this.normalizedActivitiesRating.find(r => r.star === star)?.percentage ?? 0;
      let percentage = 0;
      if (hasOrg && hasAct) {
        percentage = (orgPct + actPct) / 2;
      } else if (hasOrg) {
        percentage = orgPct;
      } else if (hasAct) {
        percentage = actPct;
      }
      return { star, percentage };
    });
  }

  get isArabic(): boolean {
    return this.currentLanguage === "ar";
  }
}
